import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const useApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getTenantEmail = () => localStorage.getItem("tenantEmail");

  // Add a single user
  const addUser = useCallback(async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/users`, userData, {
        headers: {
          "x-email": getTenantEmail(),
        },
      });
      setLoading(false);
      toast.success('User added successfully!');
      return response.data.user;
    } catch (err) {
      setLoading(false);
      const errorMessage = err.response?.data?.message || 'Failed to add user';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    }
  }, []);

  // Fetch all users
  const getUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users`, {
        headers: {
          "x-email": getTenantEmail(),
        },
      });
      setLoading(false);
      return response.data;
    } catch (err) {
      setLoading(false);
      const errorMessage = err.response?.data?.message || 'Failed to fetch users';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    }
  }, []);

  // Update a user
  const updateUser = useCallback(async (userId, userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/users/${userId}`, userData, {
        headers: {
          "x-email": getTenantEmail(),
        },
      });
      setLoading(false);
      toast.success('User updated successfully!');
      return response.data.user;
    } catch (err) {
      setLoading(false);
      const errorMessage = err.response?.data?.message || 'Failed to update user';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    }
  }, []);

  // Delete a user
  const deleteUser = useCallback(async (userId) => {
    setLoading(true);
    setError(null);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/users/${userId}`, {
        headers: {
          "x-email": getTenantEmail(),
        },
      });
      setLoading(false);
      toast.success('User deleted successfully!');
    } catch (err) {
      setLoading(false);
      const errorMessage = err.response?.data?.message || 'Failed to delete user';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    }
  }, []);

  // Bulk create users
  const bulkCreateUsers = useCallback(async (file) => {
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/users/bulk`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          "x-email": getTenantEmail(),
        },
      });
      setLoading(false);
      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.forEach((err) => {
          toast.warn(err);
        });
      }
      return response.data;
    } catch (err) {
      setLoading(false);
      const errorMessage = err.response?.data?.message || 'Failed to upload file';
      setError(errorMessage);
      toast.error(errorMessage);
      if (err.response?.data?.errors) {
        err.response.data.errors.forEach((err) => {
          toast.error(err);
        });
      }
      throw err;
    }
  }, []);

  return { addUser, getUsers, updateUser, deleteUser, bulkCreateUsers, loading, error };
};

export default useApi;